import { useState, useCallback, useRef } from 'react';
import { newsAPI } from '../lib/supabase';
import type { Company, CompanyWithProgressiveData } from '../types';

interface ProgressiveDataCache {
  [companyId: string]: {
    newsCount?: number;
    keyPointsCount?: number;
    timestamp: number;
  };
}

const CACHE_DURATION = 10 * 60 * 1000; // 10分钟缓存

export const useProgressiveCompanyDataSimple = () => {
  const [cache, setCache] = useState<ProgressiveDataCache>({});
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  // 移除 updateTrigger，直接依赖状态变化
  const loadingRef = useRef<Set<string>>(new Set()); // 防止重复加载

  // 将基础公司数据转换为渐进式数据格式
  const transformToProgressiveData = useCallback(
    (companies: Company[]): CompanyWithProgressiveData[] => {
      // 强制使用最新的状态，不依赖闭包
      const currentCache = cache;
      const currentLoadingStates = loadingStates;

      const result = companies.map(company => {
        // 优先从缓存获取，如果没有则从summary中解析sources数组长度
        let newsCount = currentCache[company.id]?.newsCount;
        let keyPointsCount = currentCache[company.id]?.keyPointsCount;
        
        // 如果缓存中没有数据，尝试从summary中解析
        if (newsCount === undefined && company.summary_24hrs) {
          try {
            const summary = JSON.parse(company.summary_24hrs);
            // 使用sources数组长度作为新闻数量
            newsCount = summary.sources && Array.isArray(summary.sources) ? summary.sources.length : 0;
            keyPointsCount = summary.analysis?.facts && Array.isArray(summary.analysis.facts) ? summary.analysis.facts.length : 0;
          } catch (e) {
            console.warn('Failed to parse summary for company:', company.name, e);
            newsCount = 0;
            keyPointsCount = 0;
          }
        }
        
        // 如果还是没有数据，使用默认值（不使用数据库的current_article_count_24hrs）
        if (newsCount === undefined) newsCount = 0;
        if (keyPointsCount === undefined) keyPointsCount = 0;

        return {
          ...company,
          newsCount,
          keyPointsCount,
          isLoadingMetrics: currentLoadingStates[company.id] || false,
        };
      });

      return result;
    },
    [cache, loadingStates]
  );

  // 简化版：逐个加载公司指标数据
  const loadCompanyMetrics = useCallback(
    async (companyIds: string[]) => {

      // 过滤出需要加载的公司ID，排除正在加载的
      const needsLoad = companyIds.filter(id => {
        if (loadingRef.current.has(id)) {
          return false;
        }
        const cached = cache[id];
        if (!cached) return true;
        return Date.now() - cached.timestamp > CACHE_DURATION;
      });

      if (needsLoad.length === 0) {
        return;
      }

      // 标记这些公司为正在加载
      needsLoad.forEach(id => loadingRef.current.add(id));

      // 设置加载状态
      setLoadingStates(prev => {
        const newStates = { ...prev };
        needsLoad.forEach(id => (newStates[id] = true));
        return newStates;
      });

      try {
        // 使用批量API：一次获取所有公司数据
        const batchSummaries = await newsAPI.getBatchCompanySummary(needsLoad);

        // 解析批量结果
        const results = needsLoad.map(companyId => {
          const summary = batchSummaries[companyId];
          let newsCount = 0;
          let keyPointsCount = 0;

          if (summary && summary.summary) {
            try {
              const summaryData = JSON.parse(summary.summary);
              // 使用sources数组长度作为新闻数量
              newsCount = summaryData.sources && Array.isArray(summaryData.sources) ? summaryData.sources.length : 0;

              if (
                summaryData.analysis &&
                summaryData.analysis.facts &&
                Array.isArray(summaryData.analysis.facts)
              ) {
                keyPointsCount = summaryData.analysis.facts.length;
              }
            } catch (error) {
              console.warn(`Failed to parse summary for company ${companyId}:`, error);
              newsCount = 0;
            }
          }

          return { status: 'fulfilled' as const, value: { companyId, newsCount, keyPointsCount } };
        });

        // 更新缓存并等待状态更新完成
        const newCacheData: ProgressiveDataCache = { ...cache };

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            const { companyId, newsCount, keyPointsCount } = result.value;
            newCacheData[companyId] = {
              newsCount,
              keyPointsCount,
              timestamp: Date.now(),
            };
          } else {
            console.error(`Failed to load company ${needsLoad[index]}:`, result.reason);
          }
        });

        // 同步更新缓存
        setCache(newCacheData);


      } catch (error) {
        console.error('Failed to load company metrics:', error);
      } finally {
        // 清除加载状态和加载标记
        setLoadingStates(prev => {
          const newStates = { ...prev };
          needsLoad.forEach(id => delete newStates[id]);
          return newStates;
        });

        // 清除加载标记
        needsLoad.forEach(id => loadingRef.current.delete(id));
      }
    },
    [cache]
  );

  return {
    transformToProgressiveData,
    loadCompanyMetrics,
    isLoading: (companyId: string) => loadingStates[companyId] || false,
    getCachedData: (companyId: string) => cache[companyId],
    clearCache: () => setCache({}),
  };
};
